import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { GenericRichTextDirective } from '@core/jss-utils';
import { EmailConsentsModalComponent } from '@innogy/become-a-customer/shared';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import {
  energyAdviceEmailRejectedFormControlID,
  setEnableAtoCheckAction,
  sustainabilityAdviceEmailRejectedFormControlID,
  type OrderConfirmationFormValues,
} from '@innogy/become-a-customer/state';
import { CommonUiFormsModule } from '@innogy/common-ui/forms';
import { CommonUiProgressiveFormsModule } from '@innogy/common-ui/progressive-forms';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { IsInvalidPipe, IsValidPipe } from '@innogy/utils-deprecated';
import {
  InfoCardComponent,
  ToolTipComponent,
} from '@legacy/common-ui-utility-components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { JssModule, type TextField } from '@sitecore-jss/sitecore-jss-angular';
import { SetValueAction } from 'ngrx-forms';

import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';

interface DisclaimerLink {
  key: TextField;
  href: string;
  automationId: string;
}

@Component({
  selector: 'wl-order-fulfillment-confirmation[progressiveFormState]',
  templateUrl: './order-fulfillment-confirmation.component.html',
  styleUrls: [
    './order-fulfillment-confirmation.component.ed.scss',
    './order-fulfillment-confirmation.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    CommonUiProgressiveFormsModule,
    CommonUiFormsModule,
    AutomationIdDirective,
    IsInvalidPipe,
    IsValidPipe,
    TranslatePipe,
    GenericRichTextDirective,
    JssModule,
    ToolTipComponent,
    InfoCardComponent,
  ],
})
export class OrderFulfillmentConfirmationComponent
  extends OrderFulfillmentStepComponent<OrderConfirmationFormValues>
  implements OnInit
{
  private readonly modalService = inject(NgbModal);
  private readonly store$ = inject(Store);
  readonly envConfig = inject(ENVIRONMENT_CONFIG);

  readonly offer = input<Offer>();

  readonly openAOMModal = output<Offer>();

  ngOnInit(): void {
    const { enableAtoCheck } = this.orderContent();

    // give access to flag in reducer in order to condtionally validate ATO Consent
    this.store$.dispatch(setEnableAtoCheckAction({ enableAtoCheck }));
  }

  override readonly progressiveFormCardContent = computed(() => ({
    title: this.orderContent().checkCardTitle,
    subtitle: this.orderContent().checkCardSubtitle,
    submitButtonText: this.orderContent().checkCardSubmitButton,
    stepNumber: this.stepNumber(),
  }));

  readonly emailConsentValues = computed(() => ({
    energyAdvice: this.formState().controls.energyAdviceEmailRejected.value,
    sustainability:
      this.formState().controls.sustainabilityAdviceEmailRejected.value,
  }));

  /**
   * Conditional dynamic rendering of the notification message
   * Preferable we don't build HTML manually but the info-card component does not support slots
   */
  readonly emailConsentMessage = computed(() => {
    let message = this.orderContent().consentsMessageBody;
    message += '<br>';
    if (
      this.emailConsentValues().energyAdvice ||
      this.emailConsentValues().sustainability
    ) {
      message += '<ul>';
      if (this.emailConsentValues().energyAdvice) {
        message += `<li>${this.orderContent().emailConsentsModalEnergyAdviceCheckbox}</li>`;
      }
      if (this.emailConsentValues().sustainability) {
        message += `<li>${this.orderContent().emailConsentsModalSustainabilityCheckbox}</li>`;
      }
      message += '</ul>';
    }
    return message;
  });

  readonly disclaimers = computed((): DisclaimerLink[] => {
    const disclaimers: DisclaimerLink[] = [
      {
        key: { value: this.orderContent().generalTermsAndConditions },
        href: this.offer()?.generalTermsAndConditionsUrl ?? '',
        automationId:
          'order-fulfillment-check-conditions-general-terms-and-conditions',
      },
      {
        key: { value: this.orderContent().qualityCriteria },
        href: this.offer()?.qualityCriteriaUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-quality-criteria',
      },
      {
        key: { value: this.orderContent().productConditions },
        href: this.offer()?.productConditionsUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-product',
      },
    ];
    const actionConditionsUrl = this.offer()?.actionConditionsUrl;
    if (actionConditionsUrl) {
      disclaimers.push({
        key: { value: this.orderContent().campaignConditions },
        href: actionConditionsUrl,
        automationId: 'order-fulfillment-check-conditions-campaign',
      });
    }

    return disclaimers;
  });

  openAOMModalClick(e: Event) {
    const offer = this.offer();
    if (!offer) {
      throw new Error('No offer found to open AOM Modal with.');
    }
    e.preventDefault();
    this.openAOMModal.emit(offer);
  }

  async openConsentsModal(event?: Event) {
    event?.preventDefault();
    const modal = this.modalService.open(EmailConsentsModalComponent, {
      centered: true,
    });
    modal.componentInstance.rendering.set(this.rendering());
    modal.componentInstance.energyAdviceEmailRejected.set(
      this.formState().value.energyAdviceEmailRejected
    );
    modal.componentInstance.sustainabilityAdviceEmailRejected.set(
      this.formState().value.sustainabilityAdviceEmailRejected
    );
    const modalResult = await modal.result;

    if (modalResult && 'energyAdviceEmailRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          energyAdviceEmailRejectedFormControlID,
          modalResult.energyAdviceEmailRejected
        )
      );
    }
    if (modalResult && 'sustainabilityAdviceEmailRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          sustainabilityAdviceEmailRejectedFormControlID,
          modalResult.sustainabilityAdviceEmailRejected
        )
      );
    }
  }
}
